import React, { useEffect, useRef, useState } from 'react';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { ViewModeDropdown } from 'components/ViewModeDropdown';
import type { ViewOption } from 'components/ViewModeDropdown';
import { PriceRangeFilter } from 'components/PriceRangeFilter/PriceRangeFilter';
import { SegmentedToggle } from 'components/SegmentedToggle';
import { useGetActivityStatsQuery } from 'store/services/activity/activityApi';
import { useActivityFeed } from 'hooks/useActivityFeed';
import { Activity } from 'types/interfaces/activity';
import ComposeTradeModal, { ComposeTradeRecipient } from 'screens/Main/tabs/TradeAlerts/components/ComposeTradeModal/ComposeTradeModal';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { ACTIVITY_TYPE_CONFIG } from './constants';
import LivePulseDashboard from './components/LivePulseDashboard';
import ActivityFilterBar from './components/ActivityFilterBar';
import ActivityFeed, { ActivityGrouping } from './components/ActivityFeed';
import { activityToStagedCard } from './activityTrade';
import './ActivityPage.css';

interface ComposeState {
  recipient: ComposeTradeRecipient;
  stagedCard: SidebarTradeCard;
}

export type ActivityViewMode = 'images-S' | 'images-M' | 'images-L' | 'list';

const VIEW_OPTIONS: ViewOption<ActivityViewMode>[] = [
  { value: 'images-S', label: 'Images (S)' },
  { value: 'images-M', label: 'Images (M)' },
  { value: 'images-L', label: 'Images (L)' },
];

const VIEW_MODE_STORAGE_KEY = 'activity-view-mode';

const getInitialViewMode = (): ActivityViewMode => {
  const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  if (saved && VIEW_OPTIONS.some(o => o.value === saved)) {
    return saved as ActivityViewMode;
  }
  return 'images-M';
};

const ActivityPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ActivityViewMode>(getInitialViewMode);
  // Grouping axis (by-card flat feed vs. per-trader groups), session-local like Trade Alerts.
  const [grouping, setGrouping] = useState<ActivityGrouping>('by-card');
  const [compose, setCompose] = useState<ComposeState | null>(null);

  const handleViewModeChange = (mode: ActivityViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  const {
    activities,
    isLoading,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount,
    activeTypes,
    selectType,
    isSocketConnected,
    newItemIds,
    clearNewItem,
    isFetchingMore,
    scrolledPendingCount,
    showBuffered,
    onScroll,
    traderBalanceMin,
    traderBalanceMax,
    setTraderBalanceMin,
    setTraderBalanceMax,
  } = useActivityFeed();

  // Single-select: the lone active filter drives the empty-state copy ("No <label>
  // activity yet.") so a category with nothing to show still explains itself.
  const activeType = Array.from(activeTypes)[0];
  const activeTypeLabel = activeType ? ACTIVITY_TYPE_CONFIG[activeType].label : '';

  // Open the compose-trade flow for a want_added/available item (send the card to the wanter).
  const handleSend = (activity: Activity) => {
    setCompose({
      recipient: { id: activity.user.id, username: activity.user.username },
      stagedCard: activityToStagedCard(activity),
    });
  };

  const {
    data: stats,
    isLoading: statsLoading,
  } = useGetActivityStatsQuery(undefined, {
    pollingInterval: isSocketConnected ? 0 : 60000,
  });

  // Bind scroll handler to .main-page-content scroll container
  const scrollBound = useRef(false);
  useEffect(() => {
    const scrollContainer = document.querySelector('.main-page-content');
    if (!scrollContainer || scrollBound.current) return;
    scrollBound.current = true;

    const handler = (e: Event) => {
      onScroll(e as unknown as React.UIEvent<HTMLElement>);
    };
    scrollContainer.addEventListener('scroll', handler, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handler);
      scrollBound.current = false;
    };
  }, [onScroll]);

  // When user clicks "new items" pill, scroll to top then flush
  const handleShowNew = () => {
    const scrollContainer = document.querySelector('.main-page-content');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    showBuffered();
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://trademagic.gg/home' },
    { name: 'Activity', url: 'https://trademagic.gg/home/activity' },
  ];

  return (
    <>
      <SEOHelmet
        title="Activity Feed - TradeMagic"
        description="Watch real-time trading activity across the TradeMagic platform. See what cards are being traded, shipped, and added to want lists."
        canonical="https://trademagic.gg/home/activity"
      />
      <BreadcrumbList breadcrumbs={breadcrumbs} />

      <div className="activity-page">
        <LivePulseDashboard
          stats={stats}
          isLoading={statsLoading}
          isConnected={isSocketConnected}
        />

        <div className="activity-page__toolbar">
          <ActivityFilterBar
            activeTypes={activeTypes}
            selectType={selectType}
            isPaused={isPaused}
            togglePause={togglePause}
            pendingCount={pendingCount}
          />
          <div className="activity-page__toolbar-controls">
            {/* Zone (b): filter by the TRADER's point balance (not card value). */}
            <PriceRangeFilter
              variant="connected"
              groupLabel="Trader balance"
              priceMin={traderBalanceMin}
              priceMax={traderBalanceMax}
              onPriceMinChange={setTraderBalanceMin}
              onPriceMaxChange={setTraderBalanceMax}
              minLabel="Trader balance (min.)"
              maxLabel="Trader balance (max.)"
            />

            <div className="activity-page__toolbar-divider" aria-hidden="true" />

            {/* Zone (c): grouping axis + image size. */}
            <div className="activity-page__toolbar-zone">
              <SegmentedToggle
                ariaLabel="Grouping"
                className="segmented-toggle--calm"
                value={grouping}
                onChange={setGrouping}
                options={[
                  { value: 'by-card', label: 'By card' },
                  { value: 'by-trader', label: 'By trader' },
                ]}
              />
              <ViewModeDropdown
                options={VIEW_OPTIONS}
                value={viewMode}
                onChange={handleViewModeChange}
              />
            </div>
          </div>
        </div>

        <ActivityFeed
          activities={activities}
          isLoading={isLoading}
          hasMore={hasMore}
          loadMore={loadMore}
          isFetchingMore={isFetchingMore}
          newItemIds={newItemIds}
          clearNewItem={clearNewItem}
          scrolledPendingCount={scrolledPendingCount}
          onShowNew={handleShowNew}
          viewMode={viewMode}
          grouping={grouping}
          onSend={handleSend}
          activeTypeLabel={activeTypeLabel}
        />
      </div>

      {compose && (
        <ComposeTradeModal
          isOpen={true}
          onClose={() => setCompose(null)}
          recipient={compose.recipient}
          initialStagedCard={compose.stagedCard}
        />
      )}
    </>
  );
};

export default ActivityPage;
